/* Vendor-agnostic biometric normalization + daily attendance processing.
 * Mirrors api/internal/biometric (Go). Same golden vectors. */
import type {
  AttendancePolicy, BioVendorInfo, DailyAttendance, DayStatus, Leave, Punch, PunchDirection, VerifyMode,
} from "../data/types";
import { hmToMin, minToHm, weekday } from "./dates";

/* ZKTeco status codes (ATTLOG col 3) — also used by BioTime, Anviz & most OEM ZK clones. */
const ZK_STATUS: Record<string, PunchDirection> = { "0": "in", "1": "out", "2": "break_out", "3": "break_in", "4": "ot_in", "5": "ot_out" };
/* ZKTeco verify codes (ATTLOG col 4) */
const ZK_VERIFY: Record<string, VerifyMode> = { "0": "pin", "1": "fingerprint", "2": "card", "3": "pin", "4": "card", "5": "fingerprint", "6": "fingerprint", "15": "face", "25": "palm", "11": "card", "9": "iris" };

const DIR_WORDS: Record<string, PunchDirection> = {
  in: "in", checkin: "in", "check-in": "in", check_in: "in", entry: "in", enter: "in", "0": "in", i: "in",
  out: "out", checkout: "out", "check-out": "out", check_out: "out", exit: "out", leave: "out", "1": "out", o: "out",
  break_out: "break_out", breakout: "break_out", "2": "break_out",
  break_in: "break_in", breakin: "break_in", "3": "break_in",
  ot_in: "ot_in", overtime_in: "ot_in", "4": "ot_in",
  ot_out: "ot_out", overtime_out: "ot_out", "5": "ot_out",
};
const VERIFY_WORDS: Record<string, VerifyMode> = {
  fingerprint: "fingerprint", finger: "fingerprint", fp: "fingerprint",
  face: "face", facial: "face", card: "card", rfid: "card", mifare: "card", pin: "pin", password: "pin",
  palm: "palm", iris: "iris",
};

export function normDirection(v: unknown): PunchDirection {
  if (v == null || v === "") return "unknown";
  const k = String(v).trim().toLowerCase().replace(/\s+/g, "_");
  return DIR_WORDS[k] || "unknown";
}
export function normVerify(v: unknown): VerifyMode {
  if (v == null || v === "") return "unknown";
  const s = String(v).trim().toLowerCase();
  if (/^\d+$/.test(s)) return ZK_VERIFY[s] || "unknown";
  return VERIFY_WORDS[s] || "unknown";
}

/** Normalize many timestamp shapes into device-local "YYYY-MM-DDTHH:mm:ss".
 *  Accepts "2026-10-03 07:41:09", "2026-10-03T07:41:09", "...Z"/offset (converted to tz offset),
 *  "2026/10/03 07:41", epoch seconds/ms (must fall in year 2000…9999 UTC — epoch 0 is rejected). Returns null when unparseable. */
export function normTime(v: unknown, tzOffsetMinutes = 180): string | null {
  if (v == null || v === "") return null;
  if (typeof v === "number" || /^\d{9,13}$/.test(String(v))) {
    let n = Number(v); if (n < 1e12) n *= 1000;
    // epoch 0 / reset device clocks / garbage: only 2000-01-01 … 9999-12-31 is a plausible punch time
    if (!(n >= EPOCH_MIN_MS && n < EPOCH_MAX_MS)) return null;
    return isoLocal(n, tzOffsetMinutes);
  }
  const s = String(v).trim().replace(/\//g, "-");
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/.exec(s);
  if (!m) return null;
  const [, Y, Mo, D, h, mi, sec, zone] = m;
  if (+Mo < 1 || +Mo > 12 || +D < 1 || +D > 31 || +h > 23 || +mi > 59 || +(sec || 0) > 59) return null;
  if (zone) {
    const off = zone === "Z" ? 0 : (zone[0] === "-" ? -1 : 1) * (parseInt(zone.slice(1, 3), 10) * 60 + parseInt(zone.slice(-2), 10));
    const utc = Date.UTC(+Y, +Mo - 1, +D, +h, +mi, +(sec || 0)) - off * 60000;
    return isoLocal(utc, tzOffsetMinutes);
  }
  return `${Y}-${Mo}-${D}T${h.padStart(2, "0")}:${mi}:${(sec || "00").padStart(2, "0")}`;
}
/** 2000-01-01T00:00:00Z and 10000-01-01T00:00:00Z in ms: numeric epochs outside this range are rejected. */
export const EPOCH_MIN_MS = 946684800000;
export const EPOCH_MAX_MS = 253402300800000;
function isoLocal(utcMs: number, off: number) { return new Date(utcMs + off * 60000).toISOString().slice(0, 19); }

export function dedupeKey(deviceId: string, deviceUserId: string, time: string, direction: string) {
  return [deviceId, deviceUserId, time, direction].join("|");
}

export interface ParsedLine { deviceUserId: string; time: string; direction: PunchDirection; verify: VerifyMode; raw: string }

/** Parse a ZKTeco PUSH ATTLOG body: lines "PIN\tYYYY-MM-DD HH:MM:SS\tStatus\tVerify\tWorkcode\t…". */
export function parseZkAttlog(body: string): { lines: ParsedLine[]; rejected: string[] } {
  const lines: ParsedLine[] = [], rejected: string[] = [];
  for (const rawLine of String(body || "").split(/\r?\n/)) {
    const raw = rawLine.trim();
    if (!raw) continue;
    const c = raw.split("\t");
    const pin = (c[0] || "").trim();
    const time = normTime(c[1]);
    if (!pin || !time) { rejected.push(raw); continue; }
    lines.push({ deviceUserId: pin, time, direction: c[2] != null ? ZK_STATUS[c[2].trim()] || "unknown" : "unknown", verify: normVerify(c[3]), raw });
  }
  return { lines, rejected };
}

/** Parse ZK USERINFO / OPERLOG "USER PIN=1\tName=Ali\t…" lines → users found on the device. */
export function parseZkUsers(body: string): { pin: string; name: string }[] {
  const out: { pin: string; name: string }[] = [];
  for (const raw of String(body || "").split(/\r?\n/)) {
    const s = raw.replace(/^USER\s+/, "").trim();
    if (!/PIN=/.test(s)) continue;
    const kv: Record<string, string> = {};
    for (const part of s.split("\t")) { const i = part.indexOf("="); if (i > 0) kv[part.slice(0, i).trim()] = part.slice(i + 1).trim(); }
    if (kv.PIN) out.push({ pin: kv.PIN, name: kv.Name || "" });
  }
  return out;
}

/** Parse a CSV export (any vendor): header row required; recognises common column names. */
export function parseCsvPunches(csv: string): { lines: ParsedLine[]; rejected: string[] } {
  const rows = String(csv || "").split(/\r?\n/).filter((r) => r.trim());
  if (!rows.length) return { lines: [], rejected: [] };
  const sep = rows[0].includes("\t") ? "\t" : rows[0].includes(";") ? ";" : ",";
  const head = rows[0].split(sep).map((h) => h.trim().toLowerCase().replace(/[^a-z]/g, ""));
  const idx = (names: string[]) => head.findIndex((h) => names.includes(h));
  const iUser = idx(["userid", "pin", "employeeid", "empid", "personid", "employeeno", "acno", "enrollnumber", "badge", "cardno", "id"]);
  const iTime = idx(["time", "datetime", "timestamp", "punchtime", "checktime", "eventtime", "logtime"]);
  const iDate = idx(["date"]);
  const iClock = idx(["clock", "hour"]);
  const iDir = idx(["direction", "status", "state", "type", "checktype", "inout", "punchstate", "attendancestatus"]);
  const iVer = idx(["verify", "verifymode", "verifytype", "mode", "method"]);
  const lines: ParsedLine[] = [], rejected: string[] = [];
  for (const raw of rows.slice(1)) {
    const c = raw.split(sep).map((x) => x.trim().replace(/^"|"$/g, ""));
    const user = iUser >= 0 ? c[iUser] : "";
    const tRaw = iTime >= 0 ? c[iTime] : iDate >= 0 && iClock >= 0 ? c[iDate] + " " + c[iClock] : "";
    const time = normTime(tRaw);
    if (!user || !time) { rejected.push(raw); continue; }
    lines.push({ deviceUserId: user, time, direction: iDir >= 0 ? normDirection(c[iDir]) : "unknown", verify: iVer >= 0 ? normVerify(c[iVer]) : "unknown", raw });
  }
  return { lines, rejected };
}

/** Convert a generic JSON webhook item from any vendor into a ParsedLine. Field aliases cover
 *  ZKBioTime, Hikvision ISAPI AcsEvent, Suprema BioStar events, Dahua, Anviz CrossChex. */
export function parseGenericEvent(o: Record<string, unknown>, tzOffsetMinutes = 180): ParsedLine | null {
  const pick = (...ks: string[]) => { for (const k of ks) { const v = o[k]; if (v != null && v !== "") return v; } return undefined; };
  const userObj = o.user_id && typeof o.user_id === "object" ? (o.user_id as Record<string, unknown>).user_id : undefined;
  const user = pick("deviceUserId", "emp_code", "employeeNoString", "employeeNo", "pin", "PIN", "userId", "user_id", "UserID", "personId", "workno", "CardNo") ?? userObj;
  const time = normTime(pick("time", "punch_time", "datetime", "dateTime", "timestamp", "Time", "CreateTime", "check_time", "eventTime", "event_time"), tzOffsetMinutes);
  if (user == null || typeof user === "object" || !time) return null;
  return {
    deviceUserId: String(user).trim(), time,
    direction: normDirection(pick("direction", "punch_state", "attendanceStatus", "status", "checktype", "Type", "inout")),
    verify: normVerify(pick("verify", "verify_type", "currentVerifyMode", "verifyMode", "Method", "verify_mode")),
    raw: JSON.stringify(o),
  };
}

/* ---------- Daily processing ---------- */
export interface ProcessInput {
  date: string; employeeIds: number[]; punches: Punch[]; leaves: Leave[]; policy: AttendancePolicy;
  /** "HH:mm" of now if date == today (enables "Not in yet"); omit for past days. */
  nowHm?: string; deviceNames?: Record<string, string>;
}

/** First-in/last-out processing: robust to devices that never send direction. */
export function processDay(inp: ProcessInput): DailyAttendance[] {
  const { date, policy } = inp;
  const start = hmToMin(policy.shiftStart), end = hmToMin(policy.shiftEnd);
  const shiftLen = end - start;
  const isWeekend = policy.weekendDays.includes(weekday(date));
  const byEmp = new Map<number, Punch[]>();
  for (const p of inp.punches) {
    if (p.employeeId == null || p.time.slice(0, 10) !== date) continue;
    const arr = byEmp.get(p.employeeId) || [];
    arr.push(p); byEmp.set(p.employeeId, arr);
  }
  return inp.employeeIds.map((id) => {
    const ps = collapse((byEmp.get(id) || []).slice().sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0)), policy.dedupeSeconds);
    const onLeave = inp.leaves.some((l) => l.employeeId === id && l.status === "Approved" && l.from <= date && l.to >= date);
    const row: DailyAttendance = { employeeId: id, date, firstIn: null, lastOut: null, workedMinutes: 0, lateMinutes: 0, overtimeMinutes: 0, punches: ps.length, status: "Absent", source: "" };
    if (ps.length) {
      const src = ps[0];
      row.source = src.source === "ess" ? "Mobile · geofence" : src.source === "manual" ? "Manual" : (inp.deviceNames && inp.deviceNames[src.deviceId]) || src.deviceId;
      const ins = ps.filter((p) => p.direction === "in" || p.direction === "ot_in");
      const outs = ps.filter((p) => p.direction === "out" || p.direction === "ot_out");
      const first = (ins[0] || ps[0]).time.slice(11, 16);
      const lastCand = outs.length ? outs[outs.length - 1] : ps.length > 1 ? ps[ps.length - 1] : null;
      row.firstIn = first;
      row.lastOut = lastCand && lastCand.time.slice(11, 16) > first ? lastCand.time.slice(11, 16) : null;
      const fi = hmToMin(first);
      row.lateMinutes = Math.max(0, fi - (start + policy.graceMinutes)) > 0 ? fi - start : 0;
      if (row.lastOut) {
        const lo = hmToMin(row.lastOut);
        row.workedMinutes = lo - fi;
        row.overtimeMinutes = Math.max(0, lo - Math.max(end, fi + shiftLen) - policy.overtimeAfterMinutes);
      }
    }
    row.status = statusOf(row, { isWeekend, onLeave, nowHm: inp.nowHm, start, grace: policy.graceMinutes, end });
    return row;
  });
}

function statusOf(r: DailyAttendance, c: { isWeekend: boolean; onLeave: boolean; nowHm?: string; start: number; grace: number; end: number }): DayStatus {
  if (c.onLeave) return "On leave";
  if (!r.firstIn) {
    if (c.isWeekend) return "Weekend";
    if (c.nowHm && hmToMin(c.nowHm) <= c.start + c.grace) return "Not in yet";
    return "Absent";
  }
  if (!r.lastOut && !(c.nowHm && hmToMin(c.nowHm) < c.end)) return "Missing punch";
  return r.lateMinutes > 0 ? "Late" : "Present";
}

/** Drop punches of the same employee closer than `sec` seconds (double taps / two devices). */
export function collapse(ps: Punch[], sec: number): Punch[] {
  const out: Punch[] = [];
  for (const p of ps) {
    const prev = out[out.length - 1];
    if (prev && secs(p.time) - secs(prev.time) < sec) continue;
    out.push(p);
  }
  return out;
}
const secs = (t: string) => Date.UTC(+t.slice(0, 4), +t.slice(5, 7) - 1, +t.slice(8, 10), +t.slice(11, 13), +t.slice(14, 16), +(t.slice(17, 19) || 0)) / 1000;

export function summarize(rows: DailyAttendance[]) {
  const s: Record<DayStatus, number> & { overtimeHours: number } = { Present: 0, Late: 0, Absent: 0, "On leave": 0, Weekend: 0, "Missing punch": 0, "Not in yet": 0, overtimeHours: 0 };
  for (const r of rows) { s[r.status]++; s.overtimeHours += r.overtimeMinutes / 60; }
  s.overtimeHours = Math.round(s.overtimeHours * 10) / 10;
  return s;
}

/** Overtime hours per employee for a payroll period (YYYY-MM) — feeds payroll adjustments. */
export function overtimeHoursForPeriod(rows: DailyAttendance[], employeeId: number, period: string): number {
  return Math.round(rows.filter((r) => r.employeeId === employeeId && r.date.startsWith(period)).reduce((a, r) => a + r.overtimeMinutes, 0) / 60 * 100) / 100;
}

export { minToHm };

/* ---------- Vendor catalog (shown in Devices → Add device wizard) ---------- */
export const VENDORS: BioVendorInfo[] = [
  { id: "zkteco", name: "ZKTeco (ADMS / PUSH)", protocols: ["push", "agent"], realtime: true,
    models: ["SpeedFace V5L", "SpeedFace V4L", "MB560-VL", "ProFace X", "uFace 800", "K40 Pro", "iClock 680", "MB20-VL", "G4L"],
    setup: ["On the device: Menu → Comm. → Cloud Server Setting", "Server address: your StartHR domain, port 443, HTTPS on", "Enable Domain Name, save and reboot", "The device appears under Pending devices — claim it with its serial number"],
    setupAr: ["في الجهاز: القائمة ← الاتصال ← إعدادات الخادم السحابي", "عنوان الخادم: نطاق ستارت HR، المنفذ 443، تفعيل HTTPS", "فعّل اسم النطاق واحفظ ثم أعد التشغيل", "يظهر الجهاز في الأجهزة المعلقة — اعتمده برقمه التسلسلي"] },
  { id: "zkbiotime", name: "ZKBioTime / BioTime 8 server", protocols: ["poll", "webhook"], realtime: false,
    models: ["BioTime 8.x", "ZKBioAccess", "ZKBio CVSecurity"],
    setup: ["BioTime → System → API: enable and create an API user", "Enter the server URL, username and password here", "StartHR polls /iclock/api/transactions/ every few minutes"],
    setupAr: ["BioTime ← النظام ← واجهة API: فعّلها وأنشئ مستخدمًا", "أدخل عنوان الخادم واسم المستخدم وكلمة المرور هنا", "يسحب ستارت HR الحركات كل بضع دقائق"] },
  { id: "suprema", name: "Suprema BioStar 2", protocols: ["poll", "webhook"], realtime: true,
    models: ["BioStation 3", "FaceStation F2", "BioEntry W3", "BioLite N2", "X-Station 2"],
    setup: ["BioStar 2 → Settings → API: enable the API server", "Create an operator account for StartHR", "Enter the BioStar URL and credentials — StartHR searches /api/events/search with a cursor"],
    setupAr: ["BioStar 2 ← الإعدادات ← API: فعّل خادم الواجهة", "أنشئ حساب مشغّل لستارت HR", "أدخل عنوان BioStar وبيانات الدخول"] },
  { id: "hikvision", name: "Hikvision (ISAPI)", protocols: ["poll", "webhook", "agent"], realtime: true,
    models: ["DS-K1T341", "DS-K1T671", "DS-K1T343", "DS-K1A802", "MinMoe series"],
    setup: ["Device web UI → Network → Advanced → HTTP Listening: set StartHR webhook URL", "Or choose Poll: StartHR calls /ISAPI/AccessControl/AcsEvent with digest auth", "LAN-only devices: install the StartHR Agent on any office PC"],
    setupAr: ["واجهة الجهاز ← الشبكة ← متقدم ← HTTP Listening: ضع رابط ستارت HR", "أو اختر السحب: يستدعي ستارت HR AcsEvent بمصادقة Digest", "للأجهزة المحلية فقط: ثبّت وكيل ستارت HR على أي حاسب بالمكتب"] },
  { id: "dahua", name: "Dahua access control", protocols: ["poll", "webhook", "agent"], realtime: false,
    models: ["ASI7213X", "ASI6214S", "ASA4214F", "ASI3204E"],
    setup: ["Enable CGI/HTTP API on the device", "Enter device URL + admin credentials", "StartHR queries recordFinder.cgi (AccessControlCardRec) incrementally"],
    setupAr: ["فعّل واجهة CGI/HTTP في الجهاز", "أدخل عنوان الجهاز وبيانات المسؤول", "يستعلم ستارت HR سجلات الدخول تدريجيًا"] },
  { id: "anviz", name: "Anviz CrossChex Cloud", protocols: ["poll"], realtime: false,
    models: ["FaceDeep 5", "W2 Face", "C2 Pro", "M7 Palm"],
    setup: ["CrossChex Cloud → API: create API key & secret", "Enter them here; StartHR pulls attendance records every few minutes"],
    setupAr: ["CrossChex Cloud ← API: أنشئ مفتاحًا وسرًّا", "أدخلهما هنا وسيسحب ستارت HR السجلات تلقائيًا"] },
  { id: "generic", name: "Any other device (webhook / CSV)", protocols: ["webhook", "file"], realtime: true,
    models: ["Matrix COSEC", "eSSL", "Realand", "Virdi", "IDEMIA MorphoAccess", "Granding", "Timmy", "Any CSV/Excel export"],
    setup: ["Webhook: POST JSON punches to the device's webhook URL with its token", "File: export attendance logs (CSV/TXT) and upload — columns are auto-detected"],
    setupAr: ["Webhook: أرسل الحركات بصيغة JSON إلى رابط الجهاز مع الرمز", "ملف: صدّر سجلات الحضور CSV/TXT وارفعها — يتم التعرف على الأعمدة تلقائيًا"] },
  { id: "agent", name: "StartHR Agent (on-premise bridge)", protocols: ["agent"], realtime: true,
    models: ["Windows / Linux / Raspberry Pi"],
    setup: ["Download the StartHR Agent and run it on an office PC on the same LAN", "Paste the agent token; add device IPs (ZK UDP/TCP 4370, Hikvision, Dahua…)", "The agent pulls logs locally and pushes signed batches to StartHR"],
    setupAr: ["نزّل وكيل ستارت HR وشغّله على حاسب في نفس الشبكة", "الصق رمز الوكيل وأضف عناوين الأجهزة", "يسحب الوكيل السجلات محليًا ويرسلها موقّعة إلى ستارت HR"] },
];
